import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaShoppingCart, FaHeart, FaSearch, FaUserCircle, FaBars, FaCoins, FaTimes, FaAngleDown, FaAngleUp, FaMapMarkerAlt, FaCheckCircle } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';

function Header({ cartItemCount = 0, onSearchClick }) {
    const navigate = useNavigate();
    const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [showMarquee, setShowMarquee] = useState(true);
    const [lastScrollY, setLastScrollY] = useState(0);

    // ── Realtime Search States ──
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const searchTimeoutRef = useRef(null);
    // ────────────────────────────

    // Toast notification state
    const [showLoginToast, setShowLoginToast] = useState(false);
    const [showNewUserBadge, setShowNewUserBadge] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            const currentScrollY = window.scrollY;

            // Marquee show/hide logic
            if (currentScrollY > lastScrollY && currentScrollY > 50) {
                setShowMarquee(false);
            } else {
                setShowMarquee(true);
            }

            setLastScrollY(currentScrollY);
            setScrolled(currentScrollY > 60);

            if (isMobileMenuOpen) {
                setIsMobileMenuOpen(false);
            }
        };

        const handleClickOutside = (event) => {
            if (isUserDropdownOpen && !event.target.closest('.user-dropdown-container')) {
                setIsUserDropdownOpen(false);
            }
            if (isMobileMenuOpen && !event.target.closest('.mobile-menu') && !event.target.closest('.mobile-menu-button')) {
                setIsMobileMenuOpen(false);
            }
            if (isMobileSearchOpen && !event.target.closest('.mobile-search-sidebar') && !event.target.closest('.mobile-search-button')) {
                setIsMobileSearchOpen(false);
            }
        };

        window.addEventListener('scroll', handleScroll);
        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            window.removeEventListener('scroll', handleScroll);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isUserDropdownOpen, isMobileMenuOpen, isMobileSearchOpen, lastScrollY]);

    // Header data state
    const [headerData, setHeaderData] = useState({
        logoUrl: '',
        companyName: 'Zero Plus Hub',
        phone: '01767298388',
        marqueeText: '🎉 25% discount use howtech26 | 🚚 Free Shipping on Orders Over ৳1000 | 🎁 Special Offer: Buy 2 Get 1 Free | ⚡ Flash Sale - Limited Time Only | 🔥 New Arrivals - Shop Now | 💳 Safe & Secure Payment | ✨ Fast Delivery Across Bangladesh | 🎉 25% discount use howtech26',
        keywords: [
            'Search Zero Plus Hub',
            'Search your best choice',
            'Keyboard',
            'Headphones',
            'Laptop',
            'Books',
            'Electronics',
            'Fashion',
            'Mobile Phones',
            'Watches'
        ]
    });

    const { isAuthenticated, userInfo, logout } = useAuth();

    // ✅ Check for just_logged_in flag
    useEffect(() => {
        const justLoggedIn = localStorage.getItem('just_logged_in');
        if (justLoggedIn === 'true' && isAuthenticated) {
            setShowLoginToast(true);
            setShowNewUserBadge(true);
            localStorage.removeItem('just_logged_in');
            setTimeout(() => setShowLoginToast(false), 4000);
            setTimeout(() => setShowNewUserBadge(false), 10000);
        }
    }, [isAuthenticated]);

    useEffect(() => {
        const fetchHeaderData = async () => {
            try {
                const response = await fetch('https://zeroplushub.sparkx-ai.shop/api/header');
                const result = await response.json();

                if (result.success && result.header) {
                    setHeaderData({
                        logoUrl: result.header.logoUrl || '',
                        companyName: result.header.companyName || 'Zero Plus Hub',
                        phone: result.header.phone || '01767298388',
                        marqueeText: result.header.marqueeText || '🎉 25% discount use howtech26 | 🚚 Free Shipping on Orders Over ৳1000 | 🎁 Special Offer: Buy 2 Get 1 Free | ⚡ Flash Sale - Limited Time Only | 🔥 New Arrivals - Shop Now | 💳 Safe & Secure Payment | ✨ Fast Delivery Across Bangladesh | 🎉 25% discount use howtech26',
                        keywords: (result.header.keywords && result.header.keywords.length > 0)
                            ? result.header.keywords
                            : [
                                'Search Zero Plus Hub',
                                'Search your best choice',
                                'Keyboard',
                                'Headphones',
                                'Laptop',
                                'Books',
                                'Electronics',
                                'Fashion',
                                'Mobile Phones',
                                'Watches'
                            ]
                    });
                }
            } catch (error) {
                console.error('Error fetching header data:', error);
            }
        };

        fetchHeaderData();
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 60);
            if (isMobileMenuOpen) {
                setIsMobileMenuOpen(false);
            }
        };

        const handleClickOutside = (event) => {
            if (isUserDropdownOpen && !event.target.closest('.user-dropdown-container')) {
                setIsUserDropdownOpen(false);
            }
            if (isMobileMenuOpen && !event.target.closest('.mobile-menu') && !event.target.closest('.mobile-menu-button')) {
                setIsMobileMenuOpen(false);
            }
        };

        window.addEventListener('scroll', handleScroll);
        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            window.removeEventListener('scroll', handleScroll);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isUserDropdownOpen, isMobileMenuOpen]);

    // ── Realtime Search Handlers ──
    const handleSearchInput = async (value) => {
        setSearchQuery(value);

        if (value.trim().length < 2) {
            setSearchResults([]);
            setShowDropdown(false);
            clearTimeout(searchTimeoutRef.current);
            return;
        }

        clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = setTimeout(async () => {
            setIsSearching(true);
            try {
                const response = await fetch(
                    `https://zeroplushub.sparkx-ai.shop/api/search/products?q=${encodeURIComponent(value.trim())}&limit=8`
                );
                const data = await response.json();
                if (data.success) {
                    setSearchResults(data.products);
                    setShowDropdown(true);
                }
            } catch (error) {
                console.error('Search error:', error);
            } finally {
                setIsSearching(false);
            }
        }, 300);
    };

    const handleProductClick = (product) => {
        const slugify = (text) =>
            text
                .toLowerCase()
                .replace(/[^\w\s-]/g, '')
                .replace(/\s+/g, '-')
                .trim();

        const slug = slugify(product.name);
        setShowDropdown(false);
        setSearchQuery('');
        setSearchResults([]);
        setIsMobileSearchOpen(false);
        navigate(`/product/${product.productId}/${slug}`);
    };

    const handleSearchBlur = () => {
        // onMouseDown এ click আগে আসে, তাই ২০০ms delay দিয়ে dropdown বন্ধ
        setTimeout(() => setShowDropdown(false), 200);
    };

    const handleSearchFocus = () => {
        if (searchResults.length > 0) setShowDropdown(true);
    };
    // ─────────────────────────────

    const handleUserIconClick = () => {
        if (isAuthenticated) {
            setIsUserDropdownOpen(prev => !prev);
        } else {
            navigate('/login');
        }
    };

    const handleLogoutClick = () => {
        logout();
        setIsUserDropdownOpen(false);
        navigate('/');
    };

    const handleMobileMenuToggle = () => {
        setIsMobileMenuOpen(prev => !prev);
    };

    const handleMobileSearchToggle = () => {
        setIsMobileSearchOpen(prev => !prev);
        // সাইডবার বন্ধ হলে search reset
        if (isMobileSearchOpen) {
            setSearchQuery('');
            setSearchResults([]);
            setShowDropdown(false);
        }
    };

    const handleNavLinkClick = () => {
        setIsMobileMenuOpen(false);
        setIsUserDropdownOpen(false);
    };

    const getUserPoints = () => {
        return userInfo && typeof userInfo.points === 'number' ? userInfo.points : 0;
    };

    const [placeholderIndex, setPlaceholderIndex] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setPlaceholderIndex((prevIndex) => (prevIndex + 1) % headerData.keywords.length);
        }, 2000);

        return () => clearInterval(interval);
    }, [headerData.keywords]);

    return (
        <>
            {/* Spacer for fixed header */}
            <div className="h-[92px] md:h-[152px]"></div>

            {/* ✅ Toast Notification */}
            {showLoginToast && (
                <div className="fixed top-20 right-4 z-[60] animate-slide-in-right">
                    <div className="bg-white rounded-lg shadow-2xl border-2 border-green-500 p-4 flex items-center gap-3 min-w-[280px]">
                        <div className="flex-shrink-0">
                            <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                                <FaCheckCircle className="text-white text-xl" />
                            </div>
                        </div>
                        <div className="flex-1">
                            <p className="text-sm font-bold text-gray-900">Welcome!</p>
                            <p className="text-xs text-gray-600">You're now logged in</p>
                        </div>
                        <button
                            onClick={() => setShowLoginToast(false)}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            <FaTimes />
                        </button>
                    </div>
                </div>
            )}

            <style jsx>{`
                @keyframes slide-in-right {
                    from { transform: translateX(100%); opacity: 0; }
                    to   { transform: translateX(0);    opacity: 1; }
                }
                .animate-slide-in-right { animation: slide-in-right 0.3s ease-out; }
            `}</style>

            <div className="fixed top-0 left-0 right-0 z-50" style={{ fontFamily: "'Assistant', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif" }}>
                {/* Marquee Banner */}
                <div className={`bg-white border-b border-gray-200 shadow-sm overflow-hidden transition-transform duration-300 ${showMarquee ? 'translate-y-0' : '-translate-y-full'}`}>
                    <div className="py-2">
                        <marquee behavior="scroll" direction="left" scrollamount="4" className="text-base" style={{ fontFamily: "'Assistant', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif" }}>
                            {headerData.marqueeText}
                        </marquee>
                    </div>
                </div>

                {/* Main Header */}
                <header className={`bg-[#131921] transition-all duration-300 ${showMarquee ? '' : '-mt-[50px]'}`}>
                    {/* Top Bar */}
                    <div className="bg-[#131921] border-b border-gray-700">
                        <div className="container mx-auto px-3 md:px-4">
                            <div className="flex items-center justify-between h-[60px]">

                                {/* Logo */}
                                <Link
                                    to="/"
                                    onClick={handleNavLinkClick}
                                    className="flex items-center px-2 py-1 border border-transparent md:hover:border-white rounded transition-all"
                                >
                                    {headerData.logoUrl && (
                                        <img
                                            src={headerData.logoUrl}
                                            alt="Logo"
                                            className="h-6 md:h-8 w-6 md:w-8 mr-1 md:mr-2 object-contain"
                                        />
                                    )}
                                    <div>
                                        <h1 className="text-base md:text-2xl font-bold text-white">
                                            {headerData.companyName}
                                        </h1>
                                        <p className="hidden sm:block text-[10px] text-gray-400">.bd</p>
                                    </div>
                                </Link>

                                {/* Phone Number (Desktop) */}
                                <div className="hidden md:flex items-center px-2 py-1 border border-transparent hover:border-white rounded transition-all cursor-pointer">
                                    <a
                                        href={`https://wa.me/${headerData.phone.replace(/[^0-9]/g, '')}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center mr-2"
                                    >
                                        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="white">
                                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                                        </svg>
                                    </a>
                                    <div className="text-xs">
                                        <div className="text-gray-300">Call us</div>
                                        <a
                                            href={`tel:${headerData.phone}`}
                                            className="text-white font-bold hover:text-[#febd69] transition-colors"
                                        >
                                            {headerData.phone}
                                        </a>
                                    </div>
                                </div>

                                {/* ── Desktop Search Bar with Realtime Dropdown ── */}
                                <div className="hidden md:flex flex-1 max-w-3xl mx-4 relative">
                                    <div className="flex w-full">
                                        <input
                                            type="text"
                                            value={searchQuery}
                                            onChange={(e) => handleSearchInput(e.target.value)}
                                            onFocus={handleSearchFocus}
                                            onBlur={handleSearchBlur}
                                            placeholder={headerData.keywords[placeholderIndex]}
                                            className="flex-1 px-4 py-2.5 rounded-l border-2 border-[#febd69] focus:border-[#f90] outline-none text-gray-900 placeholder:text-gray-400"
                                        />
                                        <button className="px-4 py-2.5 bg-[#febd69] hover:bg-[#f3a847] rounded-r transition-colors flex items-center justify-center">
                                            {isSearching
                                                ? <div className="w-5 h-5 border-2 border-[#131921] border-t-transparent rounded-full animate-spin" />
                                                : <FaSearch className="text-lg text-[#131921]" />
                                            }
                                        </button>
                                    </div>

                                    {/* Dropdown Results */}
                                    {showDropdown && searchResults.length > 0 && (
                                        <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-b-lg shadow-2xl z-50 max-h-[400px] overflow-y-auto">
                                            {searchResults.map((product) => (
                                                <div
                                                    key={product.productId}
                                                    onMouseDown={() => handleProductClick(product)}
                                                    className="flex items-center gap-3 px-4 py-3 hover:bg-orange-50 cursor-pointer border-b border-gray-100 last:border-0 transition-colors"
                                                >
                                                    {product.image && (
                                                        <img
                                                            src={product.image}
                                                            alt={product.name}
                                                            className="w-10 h-10 object-cover rounded border border-gray-200 flex-shrink-0"
                                                        />
                                                    )}
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium text-gray-900 truncate">{product.name}</p>
                                                        <p className="text-xs text-orange-600 font-semibold">৳{product.price}</p>
                                                    </div>
                                                    {product.stock > 0
                                                        ? <span className="text-xs text-green-600 flex-shrink-0">In Stock</span>
                                                        : <span className="text-xs text-red-500 flex-shrink-0">Out of Stock</span>
                                                    }
                                                </div>
                                            ))}
                                            <div className="px-4 py-2 bg-gray-50 text-center border-t border-gray-100">
                                                <span className="text-xs text-gray-500">{searchResults.length} results found</span>
                                            </div>
                                        </div>
                                    )}

                                    {/* No Results */}
                                    {showDropdown && searchResults.length === 0 && searchQuery.length >= 2 && !isSearching && (
                                        <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-b-lg shadow-2xl z-50 px-4 py-6 text-center">
                                            <p className="text-gray-500 text-sm">কোনো পণ্য পাওয়া যায়নি</p>
                                        </div>
                                    )}
                                </div>
                                {/* ──────────────────────────────────────────────── */}

                                {/* Right Section */}
                                <div className="flex items-center space-x-1 md:space-x-3">
                                    {/* Mobile Track Parcel Icon */}
                                    <Link
                                        to="/track-parcel/"
                                        onClick={handleNavLinkClick}
                                        className="md:hidden text-white px-2 py-1"
                                    >
                                        <FaMapMarkerAlt className="text-xl" />
                                    </Link>

                                    {/* Mobile Search Icon */}
                                    <button
                                        onClick={handleMobileSearchToggle}
                                        className="md:hidden mobile-search-button text-white px-2 py-1"
                                    >
                                        <FaSearch className="text-xl" />
                                    </button>

                                    {/* User Account */}
                                    <div className="relative user-dropdown-container">
                                        {isAuthenticated ? (
                                            <div className="relative">
                                                <button
                                                    onClick={handleUserIconClick}
                                                    className="flex flex-col items-center px-2 py-1 border border-transparent md:hover:border-white rounded transition-all text-white relative"
                                                >
                                                    <div className="relative">
                                                        {userInfo && userInfo.picture ? (
                                                            <img
                                                                src={userInfo.picture}
                                                                alt="User Avatar"
                                                                className="w-8 h-8 rounded-full border-2 border-white object-cover"
                                                            />
                                                        ) : (
                                                            <FaUserCircle className="text-2xl md:text-3xl" />
                                                        )}
                                                        {showNewUserBadge && (
                                                            <span className="absolute -top-1 -right-1 flex h-3 w-3">
                                                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                                                                <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500 border-2 border-white"></span>
                                                            </span>
                                                        )}
                                                    </div>
                                                    {userInfo && userInfo.name && (
                                                        <span className="text-[10px] mt-0.5 font-medium truncate max-w-[60px]">
                                                            {userInfo.name.length > 6
                                                                ? `${userInfo.name.substring(0, 6)}..`
                                                                : userInfo.name
                                                            }
                                                        </span>
                                                    )}
                                                </button>

                                                {isUserDropdownOpen && (
                                                    <div className="absolute right-0 mt-1 w-72 bg-white rounded shadow-2xl border border-gray-300 z-50">
                                                        <div className="bg-gradient-to-r from-[#febd69] to-[#f3a847] p-4 text-[#131921]">
                                                            <div className="flex items-center justify-between">
                                                                <div className="flex items-center space-x-2">
                                                                    {userInfo && userInfo.picture ? (
                                                                        <img
                                                                            src={userInfo.picture}
                                                                            alt="User Avatar"
                                                                            className="w-10 h-10 rounded-full border-2 border-white object-cover"
                                                                        />
                                                                    ) : (
                                                                        <FaUserCircle className="text-3xl" />
                                                                    )}
                                                                    <span className="font-bold">{userInfo && userInfo.name}</span>
                                                                </div>
                                                                <div className="flex items-center space-x-1 bg-white px-2 py-1 rounded">
                                                                    <FaCoins className="text-yellow-600" />
                                                                    <span className="font-bold text-sm">{getUserPoints()}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="p-2">
                                                            <div className="mb-2">
                                                                <div className="px-3 py-1 text-xs font-bold text-gray-700 uppercase">Your Account</div>
                                                                <Link to="/profile" onClick={handleNavLinkClick} className="block px-3 py-2 text-sm text-gray-800 hover:bg-gray-100 hover:text-[#C7511F] transition-colors">
                                                                    Your Profile
                                                                </Link>
                                                                <Link to="/order-history" onClick={handleNavLinkClick} className="block px-3 py-2 text-sm text-gray-800 hover:bg-gray-100 hover:text-[#C7511F] transition-colors">
                                                                    Your Orders
                                                                </Link>
                                                                <Link to="/favorites" onClick={handleNavLinkClick} className="block px-3 py-2 text-sm text-gray-800 hover:bg-gray-100 hover:text-[#C7511F] transition-colors">
                                                                    Your Wish List
                                                                </Link>
                                                            </div>
                                                            <div className="border-t pt-2">
                                                                <button
                                                                    onClick={handleLogoutClick}
                                                                    className="w-full px-3 py-2 text-sm text-left text-gray-800 hover:bg-gray-100 hover:text-red-600 transition-colors"
                                                                >
                                                                    Sign Out
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <button
                                                onClick={handleUserIconClick}
                                                className="flex items-center px-2 py-1 border border-transparent md:hover:border-white rounded transition-all text-white"
                                            >
                                                <FaUserCircle className="text-2xl md:text-3xl" />
                                            </button>
                                        )}
                                    </div>

                                    {/* Favorites */}
                                    <Link
                                        to="/favorites"
                                        onClick={handleNavLinkClick}
                                        className="hidden md:flex items-center px-2 py-1 border border-transparent md:hover:border-white rounded transition-all text-white"
                                    >
                                        <FaHeart className="text-xl md:text-2xl text-white" />
                                    </Link>

                                    {/* Cart */}
                                    <Link
                                        to="/cart"
                                        onClick={handleNavLinkClick}
                                        className="hidden md:flex relative items-center px-2 py-1 border border-transparent md:hover:border-white rounded transition-all text-white"
                                    >
                                        <div className="relative">
                                            <FaShoppingCart className="text-2xl md:text-3xl" />
                                            {cartItemCount > 0 && (
                                                <span className="absolute -top-1 left-3 md:left-4 bg-[#f08804] text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                                                    {cartItemCount}
                                                </span>
                                            )}
                                        </div>
                                        <span className="hidden md:block ml-1 text-sm font-bold">Cart</span>
                                    </Link>

                                    {/* Mobile Menu Button */}
                                    <button
                                        onClick={handleMobileMenuToggle}
                                        className="md:hidden mobile-menu-button text-white px-2 py-1"
                                    >
                                        <FaBars className="text-2xl" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Navigation Bar */}
                    <div className="hidden md:block bg-[#232F3E]">
                        <div className="container mx-auto px-4">
                            <nav className="flex items-center space-x-5 h-[60px] text-sm">
                                <button
                                    onClick={handleMobileMenuToggle}
                                    className="flex items-center px-2 py-1 border border-transparent hover:border-white rounded text-white font-bold"
                                >
                                    <FaBars className="mr-2" /> All
                                </button>
                                <Link to="/product" onClick={handleNavLinkClick} className="px-2 py-1 border border-transparent hover:border-white rounded text-white transition-all">Products</Link>
                                {isAuthenticated && (
                                    <Link to="/order-history" onClick={handleNavLinkClick} className="px-2 py-1 border border-transparent hover:border-white rounded text-white transition-all">Orders</Link>
                                )}
                                <Link to="/myorders" onClick={handleNavLinkClick} className="px-2 py-1 border border-transparent hover:border-white rounded text-white transition-all">My Orders</Link>
                                <Link to="/track-parcel/" onClick={handleNavLinkClick} className="px-2 py-1 border border-transparent hover:border-white rounded text-white transition-all">Track Parcel</Link>
                                <Link to="/about" onClick={handleNavLinkClick} className="px-2 py-1 border border-transparent hover:border-white rounded text-white transition-all">About</Link>
                                <Link to="/contact-us" onClick={handleNavLinkClick} className="px-2 py-1 border border-transparent hover:border-white rounded text-white transition-all">Contact</Link>
                                <Link to="/refund-policy" onClick={handleNavLinkClick} className="px-2 py-1 border border-transparent hover:border-white rounded text-white transition-all">Refund</Link>
                                <Link to="/faq" onClick={handleNavLinkClick} className="px-2 py-1 border border-transparent hover:border-white rounded text-white transition-all">FAQ</Link>
                            </nav>
                        </div>
                    </div>
                </header>
            </div>

            {/* ── Mobile Search Sidebar ── */}
            <div className={`mobile-search-sidebar fixed right-0 top-0 h-full w-80 bg-white shadow-2xl z-50 overflow-y-auto transition-transform duration-300 ${isMobileSearchOpen ? 'translate-x-0' : 'translate-x-full'}`}>
                <div className="bg-[#232F3E] p-4 flex items-center justify-between">
                    <h2 className="text-white font-bold text-lg">Search Products</h2>
                    <button onClick={handleMobileSearchToggle} className="text-white text-2xl">
                        <FaTimes />
                    </button>
                </div>

                <div className="p-4">
                    <div className="flex flex-col space-y-3">
                        <div className="relative">
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => handleSearchInput(e.target.value)}
                                placeholder={headerData.keywords[placeholderIndex]}
                                className="w-full px-4 py-3 rounded border-2 border-[#febd69] focus:border-[#f90] outline-none text-gray-900 placeholder:text-gray-400 pr-10"
                            />
                            {isSearching && (
                                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                    <div className="w-4 h-4 border-2 border-[#131921] border-t-transparent rounded-full animate-spin" />
                                </div>
                            )}
                        </div>

                        {/* Mobile Search Results */}
                        {searchResults.length > 0 && (
                            <div className="border border-gray-200 rounded-lg overflow-hidden shadow-md">
                                {searchResults.map((product) => (
                                    <div
                                        key={product.productId}
                                        onClick={() => { handleProductClick(product); }}
                                        className="flex items-center gap-3 px-4 py-3 hover:bg-orange-50 cursor-pointer border-b border-gray-100 last:border-0 transition-colors"
                                    >
                                        {product.image && (
                                            <img
                                                src={product.image}
                                                alt={product.name}
                                                className="w-10 h-10 object-cover rounded border border-gray-200 flex-shrink-0"
                                            />
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-gray-900 truncate">{product.name}</p>
                                            <p className="text-xs text-orange-600 font-semibold">৳{product.price}</p>
                                        </div>
                                        {product.stock > 0
                                            ? <span className="text-xs text-green-600 flex-shrink-0">✓</span>
                                            : <span className="text-xs text-red-500 flex-shrink-0">✗</span>
                                        }
                                    </div>
                                ))}
                                <div className="px-4 py-2 bg-gray-50 text-center">
                                    <span className="text-xs text-gray-500">{searchResults.length} results found</span>
                                </div>
                            </div>
                        )}

                        {/* No Results - Mobile */}
                        {searchQuery.length >= 2 && searchResults.length === 0 && !isSearching && (
                            <div className="border border-gray-200 rounded-lg px-4 py-5 text-center">
                                <p className="text-gray-500 text-sm">কোনো পণ্য পাওয়া যায়নি</p>
                            </div>
                        )}
                    </div>

                    {/* Popular Searches - search না থাকলে দেখাবে */}
                    {searchQuery.length === 0 && (
                        <div className="mt-6">
                            <h3 className="font-bold text-gray-800 mb-3">Popular Searches</h3>
                            <div className="flex flex-wrap gap-2">
                                {headerData.keywords.slice(2).map((keyword, index) => (
                                    <button
                                        key={index}
                                        onClick={() => handleSearchInput(keyword)}
                                        className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded text-sm text-gray-700 transition-colors"
                                    >
                                        {keyword}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Mobile Menu Sidebar */}
            <div className={`mobile-menu fixed left-0 top-0 h-full w-80 bg-white shadow-2xl z-50 overflow-y-auto transition-transform duration-300 ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
                <div className="bg-[#232F3E] p-4 flex items-center justify-between">
                    <div className="flex items-center space-x-2 text-white">
                        {isAuthenticated && userInfo && userInfo.picture ? (
                            <div className="relative">
                                <img src={userInfo.picture} alt="User" className="w-8 h-8 rounded-full" />
                                {showNewUserBadge && (
                                    <span className="absolute -top-1 -right-1 flex h-3 w-3">
                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                                        <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500 border-2 border-white"></span>
                                    </span>
                                )}
                            </div>
                        ) : (
                            <FaUserCircle className="text-2xl" />
                        )}
                        <span className="font-bold">
                            Hello, {isAuthenticated && userInfo && userInfo.name ? userInfo.name.split(' ')[0] : 'Sign in'}
                        </span>
                    </div>
                    <button onClick={handleMobileMenuToggle} className="text-white text-2xl">
                        <FaTimes />
                    </button>
                </div>

                <div className="p-4">
                    <div className="mb-4">
                        <h3 className="font-bold text-lg mb-2 text-gray-800">Shop by Category</h3>
                        <Link to="/product" onClick={handleNavLinkClick} className="block px-4 py-3 text-gray-700 hover:bg-gray-100 border-b border-gray-200">All Products</Link>
                        <Link to="/myorders" onClick={handleNavLinkClick} className="block px-4 py-3 text-gray-700 hover:bg-gray-100 border-b border-gray-200">My Orders</Link>
                        <Link to="/track-parcel/" onClick={handleNavLinkClick} className="block px-4 py-3 text-gray-700 hover:bg-gray-100 border-b border-gray-200">Track Parcel</Link>
                    </div>

                    <div className="mb-4">
                        <h3 className="font-bold text-lg mb-2 text-gray-800">Help & Settings</h3>
                        {isAuthenticated && (
                            <>
                                <Link to="/profile" onClick={handleNavLinkClick} className="block px-4 py-3 text-gray-700 hover:bg-gray-100 border-b border-gray-200">Your Profile</Link>
                                <Link to="/order-history" onClick={handleNavLinkClick} className="block px-4 py-3 text-gray-700 hover:bg-gray-100 border-b border-gray-200">Order History</Link>
                            </>
                        )}
                        <Link to="/about" onClick={handleNavLinkClick} className="block px-4 py-3 text-gray-700 hover:bg-gray-100 border-b border-gray-200">About Us</Link>
                        <Link to="/contact-us" onClick={handleNavLinkClick} className="block px-4 py-3 text-gray-700 hover:bg-gray-100 border-b border-gray-200">Contact Us</Link>
                        <Link to="/refund-policy" onClick={handleNavLinkClick} className="block px-4 py-3 text-gray-700 hover:bg-gray-100 border-b border-gray-200">Refund Policy</Link>
                        <Link to="/faq" onClick={handleNavLinkClick} className="block px-4 py-3 text-gray-700 hover:bg-gray-100 border-b border-gray-200">FAQ</Link>
                    </div>

                    {isAuthenticated ? (
                        <button
                            onClick={handleLogoutClick}
                            className="w-full px-4 py-3 bg-[#131921] text-white font-bold rounded hover:bg-[#232F3E] transition-colors"
                        >
                            Sign Out
                        </button>
                    ) : (
                        <button
                            onClick={() => { navigate('/login'); handleNavLinkClick(); }}
                            className="w-full px-4 py-3 bg-[#FFD814] text-[#0F1111] font-bold rounded hover:bg-[#F7CA00] transition-colors"
                        >
                            Sign in
                        </button>
                    )}
                </div>
            </div>

            {isMobileMenuOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={handleMobileMenuToggle}></div>
            )}
            {isMobileSearchOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={handleMobileSearchToggle}></div>
            )}
        </>
    );
}

export default Header;